import { appendMediaWitness, type FrameEvidenceAttempt, type MediaSegment, type MediaWitnessInput } from '@ohmyprime/segmenter';
import type { GamingSession, ProxyUploadOutcome } from './api';

/** Process-local only. No credentials, receipt authority or viewer-presentation claim. */
export interface WitnessPublisherIdentity {
  readonly generation: number;
  readonly streamId: string;
  readonly broadcastId: string;
  readonly keyId: string;
  readonly track: MediaStreamTrack;
  readonly sourceId: string;
}
export interface WitnessUploadOptions {
  enabled: boolean;
  session: GamingSession;
  /** Snapshot acquired before the source-clock start/ACK exchange. */
  identity: WitnessPublisherIdentity;
  /** Must come from the awaited authenticated measureSourceClock exchange. */
  clock: NonNullable<MediaWitnessInput['clock']>;
  /** Caller owns session/epoch/revision/scene/flag continuity through every await. */
  current(): boolean;
  deadlineMonotonicMs: number;
}
export type WitnessUploadResult =
  | { readonly status: 'uploaded'; readonly upload: ProxyUploadOutcome;readonly composition?:import('@ohmyprime/segmenter').ProgramCompositionObservation }
  | { readonly status: 'unavailable' | 'cancelled' | 'timeout' | 'failed' };

/** One main-fragment selection; failure never selects a replacement or stops ordinary media. */
export class WitnessPublication {
  readonly result: Promise<WitnessUploadResult>;
  private resolve!: (result: WitnessUploadResult) => void;
  private settled = false;
  private selected = false;
  private composition?:import('@ohmyprime/segmenter').ProgramCompositionObservation;
  private compositionCurrent?:()=>boolean;
  private timer: ReturnType<typeof setTimeout>;
  private initial = performance.now();
  constructor(
    private readonly options: WitnessUploadOptions,
    private readonly attempt: FrameEvidenceAttempt,
    private readonly videoTrackId: number,
    private readonly publisherCurrent: () => boolean,
    private readonly mapping: ReturnType<NonNullable<MediaWitnessInput["clock"]>["read"]>,
  ) {
    this.result = new Promise(resolve => { this.resolve = resolve; });
    this.timer = setTimeout(() => this.finish('timeout'), options.deadlineMonotonicMs - this.initial);
  }
  cancel(): void { this.finish('cancelled'); }
  private finish(status: Exclude<WitnessUploadResult['status'], 'uploaded'>, upload?: never): void;
  private finish(status: 'uploaded', upload: ProxyUploadOutcome): void;
  private finish(status: WitnessUploadResult['status'], upload?: ProxyUploadOutcome): void {
    if (this.settled) return;
    this.settled = true;
    clearTimeout(this.timer);
    this.attempt.end();
    this.resolve(Object.freeze(status === 'uploaded' ? { status, upload: upload!,...(this.composition?{composition:this.composition}:{}) } : { status }));
  }
  current(): boolean {
    if (this.settled) return false;
    try {
      const now = performance.now();
      if (!Number.isFinite(now) || now < this.initial || now >= this.options.deadlineMonotonicMs) {
        this.finish('timeout'); return false;
      }
      if ((this.compositionCurrent&&!this.compositionCurrent()) || !this.publisherCurrent() || !this.options.current() || !this.attempt.isCurrent()) {
        this.cancel(); return false;
      }
      const mapping = this.options.clock.read();
      if (!Object.entries(this.mapping).every(([key, value]) => mapping[key as keyof typeof mapping] === value)) {
        this.cancel(); return false;
      }
      return true;
    } catch { this.cancel(); return false; }
  }
  /** Called synchronously at the split-fragment sink, before queue admission can drop it. */
  select(segment: MediaSegment): (() => Promise<Uint8Array>) | undefined {
    if (this.selected || !this.current() || !segment.frameEvidence?.some(e => e.attempt === this.attempt)) return;
    const selected=segment.frameEvidence!.filter(e=>e.attempt===this.attempt).sort((a,b)=>a.sampleIndex-b.sampleIndex)[0]!;
    this.composition=selected.frame.composition;this.compositionCurrent=selected.frame.compositionCurrent;
    if(this.composition&&this.composition.binding.kind!=='conventional'){this.finish('failed');return;}
    this.selected = true;
    // A witness may delay media by at most one short frame budget, including queue
    // residence. The overall attempt deadline is never a media-chain wait.
    const until = Math.min(performance.now() + 16, this.options.deadlineMonotonicMs);
    let ready: Uint8Array | undefined;
    const proof = appendMediaWitness({ enabled: true, segment, attempt: this.attempt, videoTrackId: this.videoTrackId,selected, clock: this.options.clock })
      .then(bytes => { if (performance.now() <= until && this.current()) ready = bytes; })
      .catch(() => this.finish('failed'));
    return async () => {
      const remaining = until - performance.now();
      if (!ready && remaining > 0 && this.current()) {
        let timer: ReturnType<typeof setTimeout> | undefined;
        try {
          await Promise.race([proof, this.result, new Promise<void>(resolve => { timer = setTimeout(resolve, remaining); })]);
        } finally { clearTimeout(timer); }
      }
      if (ready && this.current()) return ready;
      this.finish('failed');
      return segment.bytes;
    };
  }

  observe(result: Promise<ProxyUploadOutcome> | undefined, expected: Omit<ProxyUploadOutcome, 'status' | 'uploadId'>): void {
    if (!this.current()) return;
    if (!result) { this.finish('failed'); return; }
    // This is the promise of the exact monitor observation also emitted to subscribeProxyUploads.
    // Using that promise avoids synchronous skipped-event races and duplicate-seq ambiguity.
    void result.then(upload => {
      if (!this.current()) return;
      if (!Object.entries(expected).every(([key, value]) => upload[key as keyof ProxyUploadOutcome] === value) ||
          !Number.isSafeInteger(upload.uploadId) || upload.uploadId <= 0 || upload.status !== 'uploaded') {
        this.finish('failed'); return;
      }
      this.finish('uploaded', upload);
    }).catch(() => this.finish('failed'));
  }
}
